/**
 * Мова й часовий пояс, у яких людина бачить дати й числа (UX-42, UX-43; PRF-03, PRF-04).
 *
 * Порядок, від найважливішого: власне значення людини (`GET /me`: `locale`, `timeZone`; змінює
 * `PATCH /me`) → значення компанії (`GET /settings/company`) → кеш попереднього сеансу
 * (`prefs.timeZone`) → Europe/Kyiv. Кеш потрібен першому кадру: відповіді сервера приходять після
 * малювання, і без нього людина в іншому поясі побачила б спершу київський час. Значення з сервера
 * потрапляє у `format.ts` (`setFormatContext`) і в кеш; зміна контексту перемальовує екрани
 * (`useRegionKey` з `format.ts`, у кореневих компонентах екранів), тож перезавантаження сторінки не потрібне.
 *
 * Якщо сервер недоступний, діє кеш або типове значення: форматери не кидають, екран не порожніє.
 */
import type { MeResponse, PublicCompanyProfile } from '@clientline/contracts';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useFirstScreenDone } from '../perf/first-screen.ts';
import { fetchCompanyProfile, platformKeys } from '../platform/api.ts';
import { QUIET } from '../platform/query-meta.ts';
import { queryPolicy } from '../platform/query-policy.ts';
import { applyServerPrefs, getPrefs } from '../prefs/prefs.ts';
import { meQuery } from '../shell/user.ts';
import { DEFAULT_TIME_ZONE, isKnownZone, type Locale, setFormatContext } from './format.ts';
import { canonicalZone } from './zones.ts';

/**
 * Профіль компанії для оболонки. Він лише уточнює типові значення, тож збій (403, мережа, 5xx) —
 * не помилка екрана: далі діє кеш чи Europe/Kyiv. Але й не «профілю немає»: помилка лишається
 * помилкою запиту (не кешується як успіх, новий спостерігач повторює запит, коли сервер
 * відновився), а `meta.quiet` не пускає її в «останні помилки» звернення «Повідомити про проблему»
 * (UX-51, `QueryCache.onError` у main.tsx; рев'ю пакета #351, support.spec.ts «без каналу»).
 */
export const companyProfileQuery = () =>
  queryOptions({
    queryKey: platformKeys.company,
    queryFn: ({ signal }): Promise<PublicCompanyProfile> => fetchCompanyProfile(signal),
    meta: QUIET,
    ...queryPolicy.reference,
    retry: false,
  });

/** Мови, для яких є каталог інтерфейсу. Поки одна — перемикача мови в профілі немає (PRF-03). */
export const UI_LOCALES: readonly Locale[] = ['uk'];

/** Мова з кількох джерел; невідома (наприклад, `en`, поки каталогу немає) дає українську. */
export function effectiveLocale(
  me: Pick<MeResponse, 'locale'> | undefined,
  company: Pick<PublicCompanyProfile, 'locale'> | undefined,
): Locale {
  for (const candidate of [me?.locale, company?.locale]) {
    if (candidate && (UI_LOCALES as readonly string[]).includes(candidate))
      return candidate as Locale;
  }
  return 'uk';
}

/** Пояс з кількох джерел у чинній назві IANA; невідомий рушію пояс пропускається. */
export function effectiveZone(
  me: Pick<MeResponse, 'timeZone'> | undefined,
  company: Pick<PublicCompanyProfile, 'timeZone'> | undefined,
  cached: string,
): string {
  for (const candidate of [me?.timeZone, company?.timeZone, cached]) {
    if (!candidate) continue;
    const zone = canonicalZone(candidate);
    if (isKnownZone(zone)) return zone;
  }
  return DEFAULT_TIME_ZONE;
}

// Перший кадр: пояс попереднього сеансу, ще до відповідей сервера.
setFormatContext({ timeZone: effectiveZone(undefined, undefined, getPrefs().timeZone) });

/**
 * Оболонка викликає один раз після входу: бере `/me` і профіль компанії та виставляє контекст
 * форматів. Поки відповідей немає — діє кеш; відповідь лише уточнює його. Обидва запити — після
 * межі першого екрана (perf/first-screen.ts): першому кадру досить кешу попереднього сеансу.
 */
export function useRegionSync(signedIn: boolean): void {
  const firstScreen = useFirstScreenDone();
  const enabled = signedIn && firstScreen;
  const me = useQuery({ ...meQuery(), enabled });
  const company = useQuery({ ...companyProfileQuery(), enabled });
  const meZone = me.data?.timeZone ?? null;
  const companyZone = company.data?.timeZone ?? null;
  const meLocale = me.data?.locale ?? null;
  const companyLocale = company.data?.locale ?? null;

  useEffect(() => {
    // Людина вже ввійшла, а власного поясу ще не знаємо (`/me` у дорозі): кеш не чіпаємо.
    if (!signedIn || (me.isPending && !me.isError)) return;
    // Профіль компанії потрібен, лише якщо власного поясу в людини немає.
    if (meZone === null && company.isPending && !company.isError) return;
    const timeZone = effectiveZone(
      { timeZone: meZone },
      { timeZone: companyZone ?? '' },
      getPrefs().timeZone,
    );
    const locale = effectiveLocale({ locale: meLocale }, { locale: companyLocale ?? 'uk' });
    setFormatContext({ timeZone, locale });
    applyServerPrefs({ timeZone });
  }, [
    signedIn,
    me.isPending,
    me.isError,
    company.isPending,
    company.isError,
    meZone,
    companyZone,
    meLocale,
    companyLocale,
  ]);
}
